import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrgId } from './useCurrentOrgId';
import { toast } from 'sonner';
import { getIsAppInitialized } from '../app/useAppInitialization';
import { isMailAccountUsable } from '@/lib/emailAccountConnection';
import { registerMailProviders } from '@/lib/emailengine/devSource';

interface EmailAccount {
  id: string;
  email: string;
  provider: 'gmail' | 'outlook' | 'imap';
  is_connected: boolean;
  sync_enabled: boolean;
  is_private?: boolean;
  user_id?: string;
  created_at: string;
  last_sync_at?: string;
  token_expires_at?: string;
  grant_id?: string;
  /** 'nylas' | 'emailengine' — ver src/lib/emailAccountConnection.ts. */
  mail_provider?: string;
  imap_host?: string;
  imap_username?: string;
  imap_port?: number;
  smtp_host?: string;
  smtp_username?: string;
  smtp_port?: number;
}

// Senhas IMAP/SMTP e tokens do Nylas não são legíveis por `authenticated` (grant por
// coluna, migration 20261020150000). `select('*')` falharia com "permission denied":
// liste só as colunas abaixo. Quem precisa do segredo é edge function com service_role.
// Uma string literal só (sem `+`): o supabase-js tipa o retorno a partir dela.
const EMAIL_ACCOUNT_COLUMNS = 'id, org_id, user_id, email, provider, grant_id, mail_provider, is_connected, sync_enabled, is_private, created_at, updated_at, last_sync_at, token_expires_at, imap_host, imap_port, imap_username, smtp_host, smtp_port, smtp_username, calendar_sync_enabled, primary_calendar_id, signature_html';

// Export singleton class for external access
export class AuthStateManager {
  private static instance: AuthStateManager;
  private subscribers: Set<Function> = new Set();
  private state = {
    emailAccounts: [] as EmailAccount[],
    hasConnectedAccount: false,
    loading: false
  };
  
  private lastOrgId: string | null = null;
  private isInitialized = false;
  private fetchInProgress = false;
  private cache: Map<string, { accounts: EmailAccount[], hasConnected: boolean, timestamp: number }> = new Map();
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  static getInstance() {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }
  
  subscribe(callback: Function) {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }
  
  private notify() {
    this.subscribers.forEach(callback => callback(this.state));
  }
  
  updateState(updates: Partial<typeof this.state>) {
    const newState = { ...this.state, ...updates };
    // Provedor de cada caixa para as chamadas de e-mail (nylas-* ou mail-*).
    registerMailProviders(newState.emailAccounts);

    // Verificar se realmente mudou algo antes de notificar
    const hasChanged =
      newState.emailAccounts.length !== this.state.emailAccounts.length ||
      newState.emailAccounts.some((acc, idx) => {
        const oldAcc = this.state.emailAccounts[idx];
        return !oldAcc || acc.id !== oldAcc.id || acc.is_connected !== oldAcc.is_connected ||
          acc.is_private !== oldAcc.is_private || acc.mail_provider !== oldAcc.mail_provider;
      }) ||
      newState.hasConnectedAccount !== this.state.hasConnectedAccount ||
      newState.loading !== this.state.loading;
    
    if (!hasChanged) {
      // Não atualizar nem notificar se nada mudou
      return;
    }
    
    this.state = newState;
    this.notify();
  }
  
  async fetchAccounts(orgId: string, trigger = 'unknown') {
    if (!orgId || typeof orgId !== 'string') return;

    // Check cache first
    const cached = this.cache.get(orgId);
    const now = Date.now();
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION && !trigger.includes('force')) {
      // Verificar se os dados do cache são diferentes do estado atual
      const accountsChanged =
        this.state.emailAccounts.length !== cached.accounts.length ||
        this.state.emailAccounts.some((acc, idx) => {
          const cachedAcc = cached.accounts[idx];
          return !cachedAcc || acc.id !== cachedAcc.id || acc.is_connected !== cachedAcc.is_connected ||
            acc.is_private !== cachedAcc.is_private;
        });

      const hasChanged =
        accountsChanged ||
        this.state.hasConnectedAccount !== cached.hasConnected ||
        this.state.loading !== false;

      // Só atualizar se realmente mudou
      if (hasChanged) {
        this.updateState({
          emailAccounts: cached.accounts,
          hasConnectedAccount: cached.hasConnected,
          loading: false
        });
      }

      this.lastOrgId = orgId;
      this.isInitialized = true;
      return;
    }

    // Only fetch if orgId actually changed OR this is a forced refresh
    if (this.isInitialized && this.lastOrgId === orgId && !trigger.includes('force')) return;
    if (this.fetchInProgress && !trigger.includes('force')) return;

    this.fetchInProgress = true;
    this.updateState({ loading: true });

    try {
      const { data, error } = await supabase
        .from('email_accounts')
        .select(EMAIL_ACCOUNT_COLUMNS)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const accounts = (data || []) as EmailAccount[];
      const hasValidAccount = accounts.some(account => this.hasValidConnection(account));

      // Cache the result
      this.cache.set(orgId, {
        accounts,
        hasConnected: hasValidAccount,
        timestamp: now
      });

      this.updateState({
        emailAccounts: accounts,
        hasConnectedAccount: hasValidAccount,
        loading: false
      });

      this.lastOrgId = orgId;
      this.isInitialized = true;
    } catch (error) {
      console.error('[AUTH MANAGER] Failed to fetch accounts', error);
      toast.error('Erro ao carregar contas de email');
      this.updateState({ loading: false });
    } finally {
      this.fetchInProgress = false;
    }
  }
  
  private hasValidConnection(account: EmailAccount): boolean {
    if (account.provider === 'gmail' || account.provider === 'outlook') {
      return isMailAccountUsable(account);
    }
    
    if (account.provider === 'imap') {
      return !!(account.imap_host && account.imap_username && account.is_connected);
    }
    
    return false;
  }
  
  getState() {
    return this.state;
  }
  
  forceRefresh() {
    this.isInitialized = false;
    this.lastOrgId = null;
    this.cache.clear();
  }

  clearCache() {
    this.cache.clear();
  }
}

/**
 * RADICAL AUTH MANAGER: Single source of truth for auth state
 */
export const useAuthManager = () => {
  const manager = AuthStateManager.getInstance();
  const [state, setState] = useState(manager.getState());
  const orgId = useCurrentOrgId();
  
  useEffect(() => {
    const unsubscribe = manager.subscribe(setState);
    return unsubscribe;
  }, []);
  
  // ONLY fetch when orgId actually changes and is not loading
  const lastOrgIdRef = useRef<string | null>(null);
  const hasInitialFetchRef = useRef(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (!orgId.orgId || orgId.loading) return;

    // Already processed this orgId
    if (lastOrgIdRef.current === orgId.orgId && hasInitialFetchRef.current) return;

    // App initialized + same orgId = skip
    if (getIsAppInitialized() && hasInitialFetchRef.current && lastOrgIdRef.current === orgId.orgId) return;

    // Clear previous debounce
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    // Debounce to avoid rapid re-executions
    fetchTimeoutRef.current = setTimeout(() => {
      if (lastOrgIdRef.current === orgId.orgId && hasInitialFetchRef.current) return;

      lastOrgIdRef.current = orgId.orgId;
      hasInitialFetchRef.current = true;
      manager.fetchAccounts(orgId.orgId, 'org-change');
    }, 100);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId.orgId, manager]); // orgId.loading removed: React 18 batching ensures loading is false when orgId arrives

  const refreshAccounts = useCallback((trigger: string) => {
    if (!orgId.orgId) return;
    manager.fetchAccounts(orgId.orgId, `force-${trigger}`);
  }, [orgId.orgId, manager]);
  
  const needsAuthentication = useCallback(() => {
    return !state.hasConnectedAccount;
  }, [state.hasConnectedAccount]);
  
  // Memoizar o retorno para evitar re-renders desnecessários
  return useMemo(() => ({
    emailAccounts: state.emailAccounts,
    hasConnectedAccount: state.hasConnectedAccount,
    loading: state.loading,
    needsAuthentication,
    refreshAccounts
  }), [state.emailAccounts, state.hasConnectedAccount, state.loading, needsAuthentication, refreshAccounts]);
};